/**
 * Integration of annotation properties with the Properties system
 *
 * This module provides:
 * - Central registry of all annotation property definitions
 * - AnnotationPropertyApplier component for registration and orchestration
 * - Re-exports of annotation-specific hooks from their respective files
 */

import React, { memo, useEffect } from 'react';
import { propertyRegistry, PropertyValueType } from '../index';
import { registerFloorDetectionAreaProperties } from './annotations/FloorDetectionAreaProperties';
import { ClassifyPropertyComponent } from '../../renderer/RendererRightPanel/ClassificationProperties';

// Re-export hooks for external use
export { useBoxProperties } from './annotations/BoxAnnotationProperties';
export { useRoomProperties, useRoomPropertyInputs } from './annotations/RoomAnnotationProperties';
export { useWallSketchProperties } from './annotations/WallSketchProperties';

// ============================================================================
// PROPERTY REGISTRY
// ============================================================================

/**
 * Property identifier keys for annotation properties
 * Use these constants to reference properties throughout the application
 */
export enum AnnotationPropertyKeys {
  FILL_COLOR = 'fill_color',
  HEATMAP_GRADIENT = 'heatmap_gradient',
  HEATMAP_ISOLATE = 'heatmap_isolate',
  HEATMAP_LEGEND = 'heatmap_legend',
}

// ============================================================================
// APPLIER COMPONENT
// ============================================================================

/**
 * Renderless component that manages all annotation properties
 * - Registers all annotation properties once globally with translations
 * - Syncs properties for boxes, planes, and rooms to Three.js objects
 * - Must be rendered inside RendererContext.Provider to access viewer/scene
 *
 * @example
 * ```tsx
 * <AnnotationPropertyApplier
 *   projectId={projectId}
 *   annotations={projectAnnotations}
 * />
 * ```
 */
interface AnnotationPropertyApplierProps {
  projectId: string;
}

const AnnotationPropertyApplier_: React.FC<AnnotationPropertyApplierProps> = ({ projectId: _projectId }) => {
  // Property definitions use plain English strings for name/description/tooltip.
  // These are auto-translated at the rendering layer by PropertyDefinition_ via useT().
  // Floor detection area properties register at module-import time via potree-shared,
  // but the call is preserved here as a no-op safety net in case import order changes.
  useEffect(() => {
    registerFloorDetectionAreaProperties();

    // Override the default numeric input on `annotation_class` (PointCluster classification)
    // with a colored class picker.
    propertyRegistry.setCustomComponent<PropertyValueType.INT>('annotation_class', ClassifyPropertyComponent);

    return () => {
      propertyRegistry.setCustomComponent('annotation_class', undefined);
    };
  }, []);

  return null; // Renderless component
};

export const AnnotationPropertyApplier = memo(AnnotationPropertyApplier_);
